import { Component, computed, EventEmitter, Input, model, Output, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { SpecialDateCategories, SpecialDateRow } from "../../../pojo/SpecialDate";
import { SpecialDateService } from "../../../services/special.date.service";
import { S25Util } from "../../../util/s25-util";
import { isUsFederalHolidayRow } from "./special.date.util";

@TypeManagerDecorator("s25-ng-edit-special-date")
@Component({
    selector: "s25-ng-edit-special-date",
    template: `
        @if (federalHoliday()) {
            <p class="hint">US federal holiday — only &quot;Show on calendar&quot; can be changed for this instance.</p>
        }
        <div class="flex-row">
            <label for="specialDateName">Name <span class="ngRed">*</span></label>
            <input
                id="specialDateName"
                type="text"
                class="c-input"
                [(ngModel)]="model.name"
                [disabled]="federalHoliday()"
            />
        </div>
        @if (showErrors() && !federalHoliday() && !model.name?.trim()) {
            <div class="ngRed">Name is required.</div>
        }

        <div class="flex-row">
            <label for="specialDateCategory">Category <span class="ngRed">*</span></label>
            <select
                id="specialDateCategory"
                class="cn-form__control"
                [(ngModel)]="model.categoryCode"
                [disabled]="federalHoliday()"
            >
                @for (option of categoryOptions; track option.value) {
                    <option [ngValue]="option.value">{{ option.label }}</option>
                }
            </select>
        </div>

        <div class="flex-row">
            <label>Start date <span class="ngRed">*</span></label>
            <s25-ng-editable-date
                [alwaysEditing]="!federalHoliday()"
                [(val)]="startDateVal"
                (valChange)="onStartDateChange()"
                [readOnly]="federalHoliday()"
            ></s25-ng-editable-date>
        </div>
        <div class="flex-row">
            <label>End date <span class="ngRed">*</span></label>
            <s25-ng-editable-date
                [alwaysEditing]="!federalHoliday()"
                [(val)]="endDateVal"
                [readOnly]="federalHoliday()"
            ></s25-ng-editable-date>
        </div>
        @if (showErrors() && !federalHoliday() && missingDates()) {
            <div class="ngRed">Start and end dates are required.</div>
        }
        @if (showErrors() && !federalHoliday() && !missingDates() && endBeforeStart()) {
            <div class="ngRed">End date must be on or after start date.</div>
        }

        <div class="flex-row">
            <s25-ng-checkbox [(value)]="showOnCalendar">Show on calendar</s25-ng-checkbox>
        </div>

        <s25-ng-modal-footer [type]="'save'" (save)="onSaveClick($event)" (cancel)="close.emit()"></s25-ng-modal-footer>
    `,
    styles: `
        .flex-row {
            display: flex;
            flex-direction: column;
            gap: 0.25em;
            margin-bottom: 0.75em;
        }
        .hint {
            margin-bottom: 0.75em;
            font-size: 0.9em;
        }
    `,
    standalone: false,
})
export class S25EditSpecialDateComponent {
    @Input() set specialDate(value: SpecialDateRow) {
        this.model = { ...value };
        this.startDateVal.set(value?.startDate ?? "");
        this.endDateVal.set(value?.endDate ?? "");
        this.showOnCalendar.set(value?.showOnCalendar !== false);
        this.federalHoliday.set(isUsFederalHolidayRow(value));
        this.showErrors.set(false);
    }

    @Output() saved = new EventEmitter<void>();
    @Output() close = new EventEmitter<void>();

    readonly showErrors = signal(false);
    readonly showOnCalendar = signal(true);
    readonly federalHoliday = signal(false);

    model: SpecialDateRow = {
        name: "",
        categoryCode: SpecialDateCategories.HOLIDAY,
        startDate: "",
        endDate: "",
        showOnCalendar: true,
    };

    /** Civil yyyy-MM-dd from the row, or a local calendar {@link Date} after the picker updates. */
    readonly startDateVal = model<string | Date>("");
    readonly endDateVal = model<string | Date>("");

    readonly startCivil = computed(() => S25Util.date.toCivilDateString(this.startDateVal()) ?? "");
    readonly endCivil = computed(() => S25Util.date.toCivilDateString(this.endDateVal()) ?? "");
    readonly missingDates = computed(() => !this.startCivil() || !this.endCivil());
    readonly endBeforeStart = computed(() => {
        const start = this.startCivil();
        const end = this.endCivil();
        if (!start || !end) {
            return false;
        }
        return end < start;
    });

    readonly categoryOptions = [
        { value: SpecialDateCategories.HOLIDAY, label: "Holiday" },
        { value: SpecialDateCategories.CLOSURE, label: "Closure" },
        { value: SpecialDateCategories.ACADEMIC, label: "Academic" },
    ];

    onStartDateChange() {
        if (this.federalHoliday()) {
            return;
        }
        const endCivil = S25Util.date.toCivilDateString(this.endDateVal()) ?? "";
        if (endCivil) {
            return;
        }
        const start = this.startDateVal();
        if (start) {
            this.endDateVal.set(start);
        }
    }

    async onSaveClick(stopLoading: () => void) {
        this.showErrors.set(true);
        const startCivil = this.startCivil();
        const endCivil = this.endCivil();
        this.model.startDate = startCivil ? `${startCivil}T00:00:00` : "";
        this.model.endDate = endCivil ? `${endCivil}T23:59:59` : "";
        this.model.showOnCalendar = this.showOnCalendar();
        if (!this.federalHoliday() && (!this.model.name?.trim() || this.missingDates() || this.endBeforeStart())) {
            stopLoading();
            return;
        }
        try {
            await SpecialDateService.save(this.model);
            this.saved.emit();
            this.close.emit();
        } catch (caughtError) {
            S25Util.showError(caughtError, "Something went wrong saving the special date.");
        } finally {
            stopLoading();
        }
    }
}
